import {
  CheckCircleIcon,
  CheckIcon,
  ClockIcon,
  ExamIcon,
  InfoIcon,
  PercentIcon,
  PresentationChartIcon,
  QuestionIcon,
  TargetIcon,
  TrophyIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Check, ChevronDown, ChevronRight, LockIcon, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TestListingSort from '../../components/dropdown/TestListingSort';
import TestListingLoader from '../../components/loaders/TestListingLoader';
import SyllabusModal from '../../components/modals/SyllabusModal';
import AnalysisConfirmationModal from '../../components/modals/AnalysisConfirmationModal';
import { Spoiler } from 'spoiled';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../components/ui/pagination';
import { debounce } from '../../lib/debounce';
import { useMediaQuery } from '../../utils/screenWidth';
import formatDateTime from './formatTime';
import { showTestEndedToast, showUpcomingTestToast } from './testListingToasts';
import useQuestionLevelTimerGate from './useQuestionLevelTimerGate';

const filterOptions = [
  { id: 'All', label: 'All' },
  { id: 'Not Attempted', label: 'Not Attempted' },
  { id: 'Attempted', label: 'Attempted' },
  { id: 'Upcoming', label: 'Upcoming' },
  { id: 'Resume', label: 'Resume' },
];

function UnattemptedPaperModal({ open, onOpenChange, message }) {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const modalRef = useRef(null);

  const clickHandler = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onOpenChange(false);
    }
  };

  if (!open) return null;

  if (isDesktop) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/20 bg-opacity-20"
        onClick={clickHandler}
      >
        <div ref={modalRef} className="relative bg-[var(--q3-surface-default)] rounded-2xl p-6 max-w-md w-full mx-4">
          <button
            className="absolute z-[1000] -top-12 left-1/2 -translate-x-1/2 p-2 rounded-full bg-[#161616] flex items-center justify-center cursor-pointer hover:scale-[1.01] shadow-md border border-[var(--❌-component-overlayfab-default-stroke)]"
            onClick={() => onOpenChange(false)}
          >
            <XIcon size={20} color="white" />
          </button>

          <div className="flex flex-col items-center text-center gap-3 pt-4">
            <InfoIcon size={42} color="var(--q3-neutral-light)" />

            <h3 className="text-base font-semibold text-[var(--q3-neutral-default)] leading-snug">{message}</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center backdrop-blur-sm bg-black/20 bg-opacity-20"
      onClick={clickHandler}
    >
      <div ref={modalRef} className="relative bg-[var(--q3-surface-default)] rounded-t-2xl p-6 w-full max-h-[80vh] ">
        <button
          className="absolute z-[1000] -top-12 left-1/2 -translate-x-1/2 p-2 rounded-full bg-[#161616] flex items-center justify-center cursor-pointer hover:scale-[1.01] shadow-md"
          onClick={() => onOpenChange(false)}
        >
          <XIcon size={20} color="white" />
        </button>
        <div className="flex flex-col items-center text-center gap-3 pt-4">
          <InfoIcon size={42} color="var(--q3-neutral-light)" />
          <h3 className="text-base font-semibold text-[var(--q3-neutral-default)] leading-snug">{message}</h3>
        </div>
      </div>
    </div>
  );
}

// Single test card (reused in both single and multi-subgroup views)
function TestCard({
  test,
  index,
  currentPage,
  isPackPurchased,
  selectedCategoryData,
  packId,
  navigate,
  handleViewSyllabus,
  showUpcomingTestToast,
  showTestEndedToast,
  serialNumber,
  requestAnalysisNavigation,
  guardAttempt,
}) {
  const [revealedScores, setRevealedScores] = useState({});

  const isReleased = new Date() >= new Date(test.startDate);
  let status;
  if (!isReleased) {
    status = 'not-released';
  } else if (test.isTestInProgress) {
    status = 'in-progress';
  } else if (test.isTestAttemptedByUser) {
    status = 'completed';
  } else {
    status = 'available';
  }

  const handleTestNavigation = (testId, path) => {
    const url = `/${packId}/${testId}/${path}?source=test_listing&categoryId=${encodeURIComponent(selectedCategoryData?.groupId || selectedCategoryData?.title || '')}`;
    navigate(url, {
      state: { selectedCategoryId: selectedCategoryData?.groupId },
    });
  };

  return (
    <Card
      key={test._id}
      className="border border-[var(--q3-stroke-light)] bg-[var(--q3-surface-default)] rounded-lg shadow-none py-0 overflow-hidden q3-interaction-scale-up cursor-pointer"
      onClick={() =>
        status === 'not-released'
          ? showUpcomingTestToast(test)
          : status === 'completed'
            ? requestAnalysisNavigation(test, { includeCategoryId: false })
            : new Date(test?.endDate) > new Date()
              ? guardAttempt(test, () =>
                  navigate(
                    `/${packId}/${test._id}/details?source=test_listing&categoryId=${encodeURIComponent(selectedCategoryData?.groupId || selectedCategoryData?.title || '')}`,
                  ),
                )
              : showTestEndedToast(test)
      }
    >
      <div className="flex flex-col w-full">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 px-2 lg:px-5 py-2 lg:py-6">
          <div className="flex items-center w-full lg:w-fit gap-5">
            <div
              className={`hidden lg:flex items-center justify-center w-4 h-4 ${status === 'completed' ? 'bg-[var(--q3-success-normal)]' : ''} rounded-full`}
            >
              {isPackPurchased ? (
                status === 'completed' ? (
                  <CheckIcon size={10} weight="bold" color="var(--q3-success-light)" />
                ) : (
                  <span className="font-medium text-[var(--q3-neutral-light)]">
                    {serialNumber || index + 1 + (currentPage - 1) * 15}
                  </span>
                )
              ) : (
                <LockIcon size={16} weight="bold" color="var(--q3-danger-normal)" />
              )}
            </div>
            <div className="flex flex-col w-full lg:w-fit">
              <div className="flex items-start justify-start gap-2 w-full lg:w-fit text-left">
                <h3 className="font-bold text-md">
                  {test.title}{' '}
                  {test.tags &&
                    test.tags.map((item, idx) => (
                      <Badge
                        key={idx}
                        style={{
                          backgroundColor: item.background,
                          color: item.textColor,
                          fontSize: '0.65rem',
                          padding: '2px 6px',
                          borderRadius: '6px',
                        }}
                        className="leading-none"
                      >
                        {item.title}
                      </Badge>
                    ))}
                </h3>
              </div>
              <div className="text-start mt-1 text-sm text-gray-500">
                {test.description}
                {test.isVideoSolutionAvailable && (
                  <>
                    {' • '}
                    <span className="block sm:inline">Video solution available</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex w-full lg:w-fit flex-row gap-2">
            {status === 'not-released' && (
              <>
                <Button
                  className="flex-1 rounded-full text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dimmest)]"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewSyllabus(test.title, test.syllabus);
                  }}
                >
                  View Syllabus
                </Button>
                <Button className="flex-1 rounded-full bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-dark)] text-[var(--q3-neutral-default_inverted)] opacity-30 cursor-not-allowed">
                  Attempt Test
                </Button>
              </>
            )}

            {status === 'available' && (
              <>
                <Button
                  className="flex-1 rounded-full text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dimmest)]"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewSyllabus(test.title, test.syllabus);
                  }}
                >
                  View Syllabus
                </Button>
                <Button
                  className={`flex-1 rounded-full bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-dark)] text-[var(--q3-neutral-default_inverted)] ${new Date(test?.endDate) < new Date() ? 'opacity-30 cursor-not-allowed' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (new Date(test?.endDate) < new Date()) return;
                    guardAttempt(test, () => handleTestNavigation(test._id, 'details'));
                  }}
                >
                  Attempt Test
                </Button>
              </>
            )}
            {status === 'in-progress' && (
              <>
                <Button
                  className="flex-1 rounded-full text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dimmest)]"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewSyllabus(test.title, test.syllabus);
                  }}
                >
                  View Syllabus
                </Button>
                <Button
                  className={`flex-1 rounded-full bg-[var(--q3-yellow-normal)] hover:bg-[var(--q3-yellow-dark)] text-[var(--q3-neutral-default_inverted)] ${new Date(test?.endDate) < new Date() ? 'opacity-30 cursor-not-allowed' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (new Date(test?.endDate) < new Date()) return;
                    handleTestNavigation(test._id, 'details');
                  }}
                >
                  Resume Test
                </Button>
              </>
            )}
            {status === 'completed' && (
              <>
                <Button
                  className="flex-1 rounded-full text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dimmest)]"
                  onClick={(e) => {
                    e.stopPropagation();
                    requestAnalysisNavigation(test, { includeCategoryId: true });
                  }}
                >
                  View Analysis
                </Button>
                <Button
                  className="flex-1 rounded-full bg-[var(--q3-success-normal)] hover:bg-[var(--q3-success-dark)] text-[var(--q3-neutral-default_inverted)]"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(
                      `/${packId}/${test._id}/solutions?categoryId=${selectedCategoryData?.groupId || selectedCategoryData?.title}`,
                      {
                        state: { selectedCategoryId: selectedCategoryData?.groupId },
                      },
                    );
                  }}
                >
                  View Solution
                </Button>
              </>
            )}
          </div>
        </div>

        <div
          className="px-2 lg:pl-14 lg:pr-4 flex justify-between items-center py-2 bg-[var(--q3-surface-dim)] text-[var(--q3-neutral-light)] flex-wrap"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          {status === 'not-released' && (
            <div className="flex justify-between items-center w-full flex-wrap gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-1 text-sm">
                  <QuestionIcon size={20} /> {test.totalQuestions} Qs
                </div>
                <div className="flex items-center gap-1 text-sm">
                  <ClockIcon size={20} />
                  {test.totalTime} Min
                </div>
                <div className="flex items-center gap-1 text-sm">
                  <ExamIcon size={20} /> Total Marks: {test.totalMarks}
                </div>
              </div>

              <span className="w-full sm:w-auto sm:max-w-[60%] flex justify-center sm:justify-end">
                <Badge className="bg-[var(--q3-surface-default)] border-[var(--q3-stroke-light)] text-[var(--q3-accent-normal)] text-sm font-medium max-w-full h-auto whitespace-normal text-center">
                  {test.startDateNote ? test.startDateNote : 'Releasing: ' + formatDateTime(test.startDate)}
                </Badge>
              </span>
            </div>
          )}
          {status === 'in-progress' && (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1 text-sm">
                <ClockIcon size={20} /> Time Remaining:
                <strong>{test.timeLeft} Min</strong>
              </div>
            </div>
          )}
          {status === 'completed' && (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1 text-sm">
                <CheckCircleIcon size={20} />
                <span className="hidden sm:flex">Score:</span>{' '}
                {test.otherTestsAttempted ? (
                  <strong>
                    {test.totalMarkScored}/{test.totalMarks}
                  </strong>
                ) : (
                  <Spoiler
                    density={0.5}
                    hidden={!revealedScores?.[test._id]}
                    revealOn="click"
                    className="cursor-pointer select-none"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setRevealedScores((prev) => ({ ...prev, [test._id]: true }));
                    }}
                  >
                    <strong>
                      {test.totalMarkScored}/{test.totalMarks}
                    </strong>
                  </Spoiler>
                )}
              </div>
              <div className="flex items-center gap-1 text-sm">
                <TargetIcon size={20} /> <span className="hidden sm:flex">Accuracy:</span>{' '}
                {test.otherTestsAttempted ? (
                  <strong>{test.accuracy?.toFixed(2)}%</strong>
                ) : (
                  <Spoiler
                    density={0.5}
                    hidden={!revealedScores?.[test._id]}
                    revealOn="click"
                    className="cursor-pointer select-none"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setRevealedScores((prev) => ({ ...prev, [test._id]: true }));
                    }}
                  >
                    <strong>{test.accuracy?.toFixed(2)}%</strong>
                  </Spoiler>
                )}
              </div>
              {test.leaderboardRank && (
                <div className="flex items-center gap-1 text-sm">
                  <TrophyIcon size={20} /> <span className="hidden sm:flex">Rank:</span>{' '}
                  <strong>{test.leaderboardRank}</strong>
                </div>
              )}
              {test.percentile && (
                <div className="flex items-center gap-1 text-sm">
                  <PercentIcon size={20} /> ile:{' '}
                  <strong>
                    {test.percentile
                      ? `${test.percentile?.title ?? ''} ${
                          test.percentile?.data
                            ? typeof test.percentile.data === 'string'
                              ? parseFloat(test.percentile.data).toFixed(2)
                              : test.percentile.data.toFixed(2)
                            : ''
                        }`
                      : '-'}
                  </strong>
                </div>
              )}
            </div>
          )}
          {status === 'available' && (
            <div className="flex justify-between items-center w-full flex-wrap gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-1 text-sm">
                  <QuestionIcon size={20} /> <strong>{test.totalQuestions} Qs</strong>
                </div>
                <div className="flex items-center gap-1 text-sm">
                  <ClockIcon size={20} />
                  <strong>{test.totalTime} Min</strong>
                </div>
                <div className="flex items-center gap-1 text-sm">
                  <ExamIcon size={20} /> Total Marks: <strong>{test.totalMarks}</strong>
                </div>
              </div>

              {new Date(test?.endDate) < new Date() && (
                <span className="w-full sm:w-auto flex justify-center sm:justify-end">
                  <Badge className="bg-[var(--q3-surface-default)] border-[var(--q3-stroke-light)] text-[var(--q3-accent-normal)] text-sm font-medium whitespace-nowrap">
                    Test Ended On: <strong>{formatDateTime(test?.endDate)}</strong>
                  </Badge>
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function NonChapterwiseTests({
  tests,
  chapterFilters,
  currentPage,
  totalPages,
  selectedCategoryData,
  handleChapterFilterChange,
  handleClearChapterFilters,
  handlePageChange,
  availableFilters,
  isPYQCategory,
  isPackPurchased,
  loading,
  banners,
}) {
  const navigate = useNavigate();
  const params = useParams();
  const packId = params.packId;
  const { guardAttempt, modal: questionLevelTimerModal } = useQuestionLevelTimerGate();

  const [showSyllabusModal, setShowSyllabusModal] = useState(false);
  const [syllabusModalContent, setSyllabusModalContent] = useState({ title: '', syllabus: '' });
  const [localSearch, setLocalSearch] = useState(chapterFilters.search || '');
  const [selectedSortOption, setSelectedSortOption] = useState(null);
  const [analysisConfirmation, setAnalysisConfirmation] = useState({
    isOpen: false,
    url: '',
    navState: undefined,
  });
  const [unattemptedModalOpen, setUnattemptedModalOpen] = useState(false);
  const [unattemptedModalMessage, setUnattemptedModalMessage] = useState('');

  const [scrollState, setScrollState] = useState({ canScrollLeft: false, canScrollRight: true });
  const scrollRef = useRef(null);

  const isMultiSubgroup = useMemo(() => tests.length > 1, [tests]);
  const [expandedSubgroups, setExpandedSubgroups] = useState(new Set());

  const toggleSubgroup = (index) => {
    const newExpanded = new Set(expandedSubgroups);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedSubgroups(newExpanded);
  };

  const handleViewSyllabus = (title, syllabusHtml) => {
    setSyllabusModalContent({ title, syllabus: syllabusHtml });
    setShowSyllabusModal(true);
  };

  useEffect(() => {
    checkScrollState();
  }, []);

  const checkScrollState = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setScrollState({
        canScrollLeft: scrollLeft > 0,
        canScrollRight: scrollLeft < scrollWidth - clientWidth - 1,
      });
    }
  };

  const scrollFilters = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 200;
      scrollRef.current.scrollTo({
        left:
          direction === 'left'
            ? scrollRef.current.scrollLeft - scrollAmount
            : scrollRef.current.scrollLeft + scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const getMaskStyle = () => {
    const { canScrollLeft, canScrollRight } = scrollState;
    if (!canScrollLeft && !canScrollRight) return {};
    if (!canScrollLeft && canScrollRight)
      return {
        maskImage: 'linear-gradient(to right, black 90%, transparent)',
        WebkitMaskImage: 'linear-gradient(to right, black 90%, transparent)',
      };
    if (canScrollLeft && !canScrollRight)
      return {
        maskImage: 'linear-gradient(to right, transparent, black 10%)',
        WebkitMaskImage: 'linear-gradient(to right, transparent, black 10%)',
      };
    return {
      maskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
      WebkitMaskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
    };
  };

  useEffect(() => {
    setLocalSearch(chapterFilters.search || '');
  }, [chapterFilters.search]);

  useEffect(() => {
    handleChapterFilterChange({ sorting: selectedSortOption });
  }, [selectedSortOption]);

  const debouncedFilterChange = useMemo(
    () =>
      debounce((searchValue) => {
        handleChapterFilterChange({ search: searchValue });
      }, 400),
    [handleChapterFilterChange],
  );

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setLocalSearch(value);
    debouncedFilterChange(value);
  };

  const handleTestNavigation = (testId, path) => {
    const url = `/${packId}/${testId}/${path}?source=test_listing&categoryId=${selectedCategoryData?.groupId || selectedCategoryData?.title}`;
    navigate(url, {
      state: { selectedCategoryId: selectedCategoryData?.groupId },
    });
  };

  const requestAnalysisNavigation = (test, { includeCategoryId } = {}) => {
    const url = includeCategoryId
      ? `/${packId}/${test._id}/analysis?source=test_listing&categoryId=${
          selectedCategoryData?.groupId || selectedCategoryData?.title
        }`
      : `/${packId}/${test._id}/analysis?source=test_listing`;

    const navState = includeCategoryId ? { selectedCategoryId: selectedCategoryData?.groupId } : undefined;
    if (!test?.otherTestsAttempted) {
      setAnalysisConfirmation({ isOpen: true, url, navState });
      return;
    }

    navigate(url, navState ? { state: navState } : undefined);
  };

  const handleViewOverallAnalysis = (e, subgroup, subgroupIndex) => {
    e.stopPropagation();

    const firstTest = subgroup.tests[0]?.test;
    const isFirstTestReleased = new Date() >= new Date(firstTest?.startDate);

    if (!isFirstTestReleased) {
      setUnattemptedModalMessage(`${subgroup.title} will be available on ${formatDateTime(firstTest?.startDate)}`);
      setUnattemptedModalOpen(true);
      return;
    }

    const unattemptedTests = subgroup.tests.filter((td) => !td.test.isTestAttemptedByUser);

    if (unattemptedTests.length > 1) {
      setUnattemptedModalMessage(`Please attempt ${subgroup.title} to view Overall Analysis`);
      setUnattemptedModalOpen(true);
      return;
    } else if (unattemptedTests.length === 1) {
      const singleUnattempted = unattemptedTests[0];
      setUnattemptedModalMessage(`Please attempt ${singleUnattempted.test.shortTitle} to view Overall Analysis`);
      setUnattemptedModalOpen(true);
      return;
    }

    const testIdOne = subgroup.tests[0]?.test?._id;
    const testIdTwo = subgroup.tests[1]?.test?._id;
    navigate(`/${packId}/${testIdOne}/${testIdTwo}/analysis`);
  };

  const sharedTestCardProps = {
    currentPage,
    isPackPurchased,
    selectedCategoryData,
    packId,
    navigate,
    handleViewSyllabus,
    showUpcomingTestToast,
    showTestEndedToast,
    guardAttempt,
  };

  const EmptyTests = () => (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="mb-4">
        <Search className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-[var(--q3-neutral-default)] mb-2">😕 No tests found</h3>
        <p className="text-[var(--q3-neutral-light)] mb-6">Try adjusting your search or filter criteria</p>
      </div>
      <Button
        onClick={() => {
          handleClearChapterFilters();
          setSelectedSortOption(null);
        }}
        className="rounded-full bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-dark)] transition-colors duration-300"
      >
        Clear Filters
      </Button>
    </div>
  );

  return (
    <div className="max-w-[1000px] mx-auto text-center md:pr-3">
      <div className="q3-glass p-4 pt-0 -mt-1 -mx-4 md:-mx-4 lg:-mx-4 rounded-xl">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <h2 className="text-2xl font-bold">{selectedCategoryData?.title}</h2>
          <div className="relative w-full md:w-56 mt-2 md:mt-0 bg-[var(--q3-surface-glass)] border-[var(--q3-stroke-normal)] rounded-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search Tests"
              className="pl-10 rounded-full"
              value={localSearch}
              onChange={handleSearchInput}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-4 mb-2 overflow-x-auto">
          <div className="relative">
            <div
              ref={scrollRef}
              className="flex gap-2 rounded-[12px] overflow-x-auto overflow-y-hidden whitespace-nowrap relative"
              style={getMaskStyle()}
              onScroll={checkScrollState}
            >
              <div className="flex gap-2 flex-shrink-0">
                <TestListingSort
                  setSelectedSortOption={setSelectedSortOption}
                  selectedSortOption={selectedSortOption}
                />
                <div className="border border-l-[1px] border-[var(--q3-stroke-normal)]"></div>
                {filterOptions?.length > 0 && (
                  <>
                    {filterOptions.map((status) => (
                      <Button
                        key={status.id}
                        className={`rounded-full capitalize flex-shrink-0 ${chapterFilters.testStatus === status.id ? 'bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-normal)]' : 'bg-[var(--q3-surface-glass)] hover:bg-[var(--q3-accent-light)] text-[var(--q3-neutral-default)] border border-[var(--q3-stroke-normal)]'}`}
                        onClick={() => {
                          handleChapterFilterChange({
                            testStatus: chapterFilters.testStatus === status.id ? null : status.id,
                          });
                        }}
                      >
                        {status.label}
                        {chapterFilters.testStatus === status.id && <Check className="ml-1 h-3 w-3" />}
                      </Button>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* {availableFilters && availableFilters.selectUnit && availableFilters.selectUnit.length > 2 && (
            <Select value={chapterFilters.selectUnit || 'All'}>
              <SelectTrigger className="w-48 rounded-full">
                <SelectValue placeholder="Select Unit" />
              </SelectTrigger>
              <SelectContent>
                {availableFilters.selectUnit.map((unit) => (
                  <SelectItem key={unit.unit} value={unit.unit}>
                    {unit.unit}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )} */}
        </div>
      </div>
      {/* <div
        className="text-start bg-[var(--q3-surface-dim)] border-l-2 border-[var(--q3-accent-normal)] p-4 rounded-r-lg mb-6 flex flex-col lg:flex-row lg:items-center cursor-pointer gap-4"
        onClick={() => navigate(`/v2/${packId}/analysis/performance`)}
      >
        <div className="flex items-center flex-1">
          <PresentationChartIcon className="size-16 lg:size-8 mx-2 text-[var(--q3-accent-normal)]" />
          <span className="px-2">
            Check out the JEE Main 2025 Test Analysis to evaluate your performance more effectively.
          </span>
        </div>

        <div className="flex justify-center lg:justify-end">
          <button className="relative inline-flex h-12 min-w-fit overflow-hidden rounded-full p-[3px] focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:ring-offset-slate-50 whitespace-nowrap cursor-pointer">
            <span className="absolute inset-[-1000%] animate-[spin_2s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,var(--q3-accent-normal)_0%,var(--q3-danger-light)_50%,var(--q3-accent-normal)_100%)]" />
            <span className="inline-flex h-full w-full items-center justify-center rounded-full bg-[var(--q3-surface-dim)] px-6 py-1 text-sm font-medium text-[var(--q3-accent-normal)] backdrop-blur-3xl whitespace-nowrap">
              View Pack Analysis
            </span>
          </button>
        </div>
      </div> */}
      <div className="relative z-0">
        {banners &&
          banners.length > 0 &&
          banners
            .filter((banner) => {
              return (
                !banner.groupIds ||
                banner.groupIds?.length === 0 ||
                banner.groupIds.includes(selectedCategoryData?.groupId)
              );
            })
            .map((banner, index) => (
              <div
                key={index}
                className={`bg-[${banner.bgColor}] border-l-2 border-[${banner.borderColor}] text-[${banner.textColor}] p-4 rounded-r-lg mb-6 flex items-center justify-between`}
              >
                <div className="flex-1 text-left">{banner.content}</div>
                {banner.isButtonEnabled && (
                  <div className="ml-4">
                    <Button
                      className={`bg-[${banner.buttonBgColor}] text-[${banner.buttonTextColor}] hover:bg-[${banner.buttonBgColor}]`}
                      onClick={() => window.open(banner.url, '_blank')}
                    >
                      {banner.buttonText}
                    </Button>
                  </div>
                )}
              </div>
            ))}
        {loading ? (
          <TestListingLoader />
        ) : (
          <div className="space-y-4 py-3">
            {isMultiSubgroup ? (
              tests.length > 0 ? (
                <div className="w-full space-y-4">
                  {tests.map((subgroup, i) => (
                    <div key={i} className="border rounded-lg overflow-hidden">
                      <div
                        onClick={() => toggleSubgroup(i)}
                        className="px-5 py-4 hover:no-underline group transition-colors duration-300 ease-in-out hover:bg-[var(--q3-surface-dimmer)] cursor-pointer "
                      >
                        <div className="flex items-center gap-4 w-full">
                          <div
                            className="flex items-center justify-center w-9 h-8 rounded-full text-[var(--q3-neutral-default)] bg-[var(--q3-surface-dimmer)] transition-colors duration-300"
                            style={
                              expandedSubgroups.has(i)
                                ? {
                                    backgroundColor: 'var(--q3-accent-normal)',
                                    color: 'var(--q3-neutral-default_inverted)',
                                  }
                                : {}
                            }
                          >
                            <span className="font-medium">{i + 1 + (currentPage - 1) * 15}</span>
                          </div>

                          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 w-full">
                            <div className="flex flex-col items-start flex-1 min-w-0">
                              <h3 className="font-bold text-lg text-left">{subgroup.title}</h3>
                              <p className="text-sm text-gray-500">
                                Total {subgroup.tests.length} paper{subgroup.tests.length > 1 ? 's' : ''}
                              </p>
                            </div>

                            {subgroup.showCombinedAnalysisButton && (
                              <Button
                                className="rounded-full text-[var(--q3-accent-normal)] bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dimmest)] flex items-center gap-2 text-sm shrink-0"
                                onClick={(e) => handleViewOverallAnalysis(e, subgroup, i)}
                              >
                                <PresentationChartIcon size={16} />
                                View Overall Analysis
                                <ChevronRight className="w-4 h-4" />
                              </Button>
                            )}
                          </div>

                          <div
                            className={`transition-transform duration-300 flex-shrink-0 border border-[var(--q3-stroke-normal)] rounded-full p-1 ${expandedSubgroups.has(i) ? 'rotate-180 bg-[var(--q3-surface-normal-default)]' : 'bg-[var(--q3-accent-light)]'}`}
                          >
                            <ChevronDown size={20} className="text-[var(--q3-neutral-default)]" />
                          </div>
                        </div>
                      </div>

                      {expandedSubgroups.has(i) && (
                        <div className="px-5 pb-4 pt-1">
                          <div className="space-y-6">
                            {subgroup.tests.map((testData, index) => (
                              <TestCard
                                key={testData.test._id}
                                test={testData.test}
                                index={index}
                                serialNumber={`P${index + 1}`}
                                requestAnalysisNavigation={requestAnalysisNavigation}
                                {...sharedTestCardProps}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyTests />
              )
            ) : (
              <div className="space-y-8">
                {tests.length > 0 && tests[0]?.tests?.length > 0 ? (
                  tests[0].tests.map((testData, index) => (
                    <TestCard
                      key={testData.test._id}
                      test={testData.test}
                      index={index}
                      {...sharedTestCardProps}
                      requestAnalysisNavigation={requestAnalysisNavigation}
                    />
                  ))
                ) : (
                  <EmptyTests />
                )}
              </div>
            )}
          </div>
        )}

        {totalPages > 1 && !loading && (
          <div className="mt-6">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    aria-disabled={currentPage === 1}
                    className={currentPage === 1 ? 'pointer-events-none opacity-50' : ''}
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage > 1) handlePageChange(currentPage - 1);
                    }}
                  />
                </PaginationItem>

                {[...Array(totalPages).keys()].map((page) => (
                  <PaginationItem key={page + 1}>
                    <PaginationLink
                      href="#"
                      isActive={currentPage === page + 1}
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(page + 1);
                      }}
                    >
                      {page + 1}
                    </PaginationLink>
                  </PaginationItem>
                ))}

                <PaginationItem>
                  <PaginationNext
                    href="#"
                    aria-disabled={currentPage === totalPages}
                    className={currentPage === totalPages ? 'pointer-events-none opacity-50' : ''}
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage < totalPages) handlePageChange(currentPage + 1);
                    }}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}

        <SyllabusModal
          open={showSyllabusModal}
          onOpenChange={setShowSyllabusModal}
          dialogContent={syllabusModalContent}
        />
        <AnalysisConfirmationModal
          isOpen={analysisConfirmation.isOpen}
          onClose={() => setAnalysisConfirmation({ isOpen: false, url: '', navState: undefined })}
          onConfirm={() => {
            const { url, navState } = analysisConfirmation;
            setAnalysisConfirmation({ isOpen: false, url: '', navState: undefined });
            navigate(url, navState ? { state: navState } : undefined);
          }}
        />
      </div>
      <UnattemptedPaperModal
        open={unattemptedModalOpen}
        onOpenChange={setUnattemptedModalOpen}
        message={unattemptedModalMessage}
      />
      {questionLevelTimerModal}
    </div>
  );
}

export default NonChapterwiseTests;
